import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Logo } from './Brand';
import { useAuth } from '../AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { LogIn, LogOut, LayoutDashboard, Menu, ChevronDown, Facebook } from 'lucide-react';
import { trackCtaClick } from '../lib/analytics';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Location dropdown items only — Example Website is now a top-level Link
// pointing to the dedicated /example-website page.
const LOCATION_LINKS = [
  { to: '/dog-training-software',           label: 'All 50 States',   testid: 'location-all' },
  { to: '/dog-training-software/canada',    label: 'Canada',          testid: 'location-canada' },
  { to: '/dog-training-software/uk',        label: 'United Kingdom',  testid: 'location-uk' },
  { to: '/dog-training-software/worldwide', label: 'Worldwide',       testid: 'location-worldwide' },
];

export const PublicNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = [
    { to: '/', label: 'Home', testid: 'nav-home-link-text' },
    { to: '/example-website', label: 'Example Website', testid: 'nav-example-website-link' },
    { to: '/#team', label: 'Team' },
    { to: '/#workshop', label: 'Upcoming Events', testid: 'nav-events-link', pulse: true },
    { to: '/compare', label: 'Compare', testid: 'nav-compare-link' },
    { to: '/services', label: 'Services', testid: 'nav-services-link' },
    { to: '/pricing', label: 'Pricing', testid: 'nav-pricing-link' },
    { to: '/blog', label: 'Blog', testid: 'nav-blog-link' },
    { to: '/contact', label: 'Book a Demo', testid: 'nav-contact-link' },
  ];
  return (
    <header
      className='jfk-public-nav sticky top-0 z-40 bg-white dark:bg-[#0B1220] border-b border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-[0_1px_0_0_rgba(147,197,253,0.08)]'
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid='public-nav'
    >
      <div className='max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-12 sm:h-16 flex items-center justify-between gap-2 sm:gap-3'>
        <Link to='/' className='flex items-center' data-testid='nav-home-link'>
          <Logo />
        </Link>
        {/* Slim laptop nav — shows on XL screens (1280–1535px) only,
            hidden on 2XL where the full nav appears and on smaller widths
            where the hamburger sheet takes over. Surfaces the four
            highest-value top-level links + Book a Demo pill so laptop
            visitors get one-tap access to the most-clicked pages without
            having to open the hamburger. The hamburger button on the
            right stays visible in this band so the full nav (Home, Team,
            Upcoming Events, Location, Blog) is still one tap away. */}
        <nav className='hidden xl:flex 2xl:hidden items-center gap-5 text-sm font-semibold text-slate-800 dark:text-slate-100'>
          {/* Location — dropdown listing 50 States index + Canada + UK +
              Worldwide. Same onSelect+navigate pattern used in the full
              nav so Radix releases the pointer-events lock before the
              route change fires. Replaces the old "Example Website"
              slot in this slim band. */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className='inline-flex items-center gap-1 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors focus:outline-none'
              data-testid='nav-slim-location-trigger'
            >
              Location <ChevronDown className='w-3.5 h-3.5' />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='center'
              className='min-w-[200px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'
              data-testid='nav-slim-location-menu'
            >
              {LOCATION_LINKS.map((l) => (
                <DropdownMenuItem
                  key={'slim-' + l.to + l.label}
                  onSelect={(e) => {
                    e.preventDefault();
                    requestAnimationFrame(() => navigate(l.to));
                  }}
                  className='w-full cursor-pointer text-slate-800 dark:text-slate-100 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] focus:text-[#1B3A87] dark:focus:text-[#93C5FD]'
                  data-testid={`slim-${l.testid}`}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link
            to='/pricing'
            onClick={() => trackCtaClick('pricing', 'public_nav_slim')}
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-slim-pricing-link'
          >
            Pricing
          </Link>
          <Link
            to='/compare'
            onClick={() => trackCtaClick('compare', 'public_nav_slim')}
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-slim-compare-link'
          >
            Compare
          </Link>
          <Link
            to='/contact'
            onClick={() => trackCtaClick('book_a_demo', 'public_nav_slim')}
            className='inline-flex items-center justify-center whitespace-nowrap px-4 py-1.5 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-xs font-bold shadow-md leading-none'
            data-testid='nav-slim-contact-link'
          >
            Book a Demo
          </Link>
        </nav>

        {/* Middle nav — shows on 2XL screens (>=1536px) only. Below that
            the row gets crowded and the right-side Sign in / Get Started
            buttons get pushed off-screen, so we collapse everything into
            the hamburger sheet instead. Gap tightened to gap-5 so the
            full set (8 links + Book a Demo pill) fits comfortably without
            touching the right-hand cluster. */}
        <nav className='hidden 2xl:flex items-center gap-5 text-sm font-semibold text-slate-800 dark:text-slate-100'>
          <Link to='/' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors' data-testid='nav-home-link-text'>Home</Link>

          {/* Example Website — top-level link to the dedicated demo page
              (previously an "Explore" dropdown listing Board & Train,
              Memberships, Financing, etc.). Those sections now live at
              /example-website. */}
          <Link
            to='/example-website'
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-example-website-link'
          >
            Example Website
          </Link>

          <Link to='/#team' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'>Team</Link>
          <Link
            to='/#workshop'
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors inline-flex items-center gap-1.5'
            data-testid='nav-events-link'
          >
            Upcoming Events
            <span
              aria-hidden='true'
              className='w-1.5 h-1.5 rounded-full bg-[#C8102E]'
              style={{ boxShadow: '0 0 0 0 rgba(200,16,46,0.55)', animation: 'k9-workshop-live-pulse 1.8s ease-out infinite' }}
            />
          </Link>
          {/* Location — dropdown with the states index + Canada + UK + Worldwide.
              Same onSelect+navigate pattern as Examples to avoid the Radix
              pointer-events lock. */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className='inline-flex items-center gap-1 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors focus:outline-none'
              data-testid='nav-location-trigger'
            >
              Location <ChevronDown className='w-3.5 h-3.5' />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='center'
              className='min-w-[200px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'
              data-testid='nav-location-menu'
            >
              {LOCATION_LINKS.map((l) => (
                <DropdownMenuItem
                  key={l.to + l.label}
                  onSelect={(e) => {
                    e.preventDefault();
                    requestAnimationFrame(() => navigate(l.to));
                  }}
                  className='w-full cursor-pointer text-slate-800 dark:text-slate-100 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] focus:text-[#1B3A87] dark:focus:text-[#93C5FD]'
                  data-testid={l.testid}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link
            to='/compare'
            onClick={() => trackCtaClick('compare', 'public_nav')}
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-compare-link'
          >
            Compare
          </Link>
          <Link
            to='/pricing'
            onClick={() => trackCtaClick('pricing', 'public_nav')}
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-pricing-link'
          >
            Pricing
          </Link>
          <Link
            to='/blog'
            onClick={() => trackCtaClick('blog', 'public_nav')}
            className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD] transition-colors'
            data-testid='nav-blog-link'
          >
            Blog
          </Link>
          <Link
            to='/contact'
            onClick={() => trackCtaClick('book_a_demo', 'public_nav')}
            className='k9-demo-cta inline-flex items-center justify-center whitespace-nowrap px-5 py-2 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg text-center leading-none'
            data-testid='nav-contact-link'
          >
            Book a Demo
          </Link>
        </nav>
        <div className='flex items-center gap-1.5 sm:gap-2 shrink-0'>
          {/* Facebook — circular icon button, matches the theme toggle size. */}
          <a
            href='https://www.facebook.com/profile.php?id=61592826938297'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Follow K9os on Facebook'
            data-testid='nav-facebook-button'
            className='w-8 h-8 sm:w-9 sm:h-9 inline-flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:bg-[#1465D6] hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(24,119,242,0.6)] transition-all'
          >
            <Facebook className='w-3.5 h-3.5 sm:w-4 sm:h-4' fill='currentColor' strokeWidth={0} />
          </a>
          <ThemeToggle size='sm' />
          {user ? (
            <>
              <Button
                variant='outline'
                size='sm'
                className='hidden sm:inline-flex border-slate-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
                onClick={() => navigate(user.role === 'CLIENT' ? '/client' : '/admin')}
                data-testid='nav-dashboard-button'
              >
                <LayoutDashboard className='w-4 h-4 mr-1.5' /> Dashboard
              </Button>
              <Button
                variant='ghost'
                size='sm'
                className='hidden sm:inline-flex dark:text-slate-200 dark:hover:bg-slate-800'
                onClick={() => { logout(); navigate('/'); }}
                data-testid='nav-logout-button'
              >
                <LogOut className='w-4 h-4 mr-1.5' /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='outline'
                size='sm'
                className='hidden sm:inline-flex border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-[#1B3A87] dark:hover:border-[#93C5FD]'
                onClick={() => navigate('/login')}
                data-testid='nav-login-button'
              >
                <LogIn className='w-4 h-4 mr-1.5' /> Sign in
              </Button>
              <Button
                size='sm'
                className='hidden sm:inline-flex bg-[#1B3A87] hover:bg-[#16306F] text-white'
                onClick={() => navigate('/register')}
                data-testid='nav-register-button'
              >
                Get Started
              </Button>
            </>
          )}
          {/* Mobile sheet */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                className='2xl:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border-2 border-[#0B1220] dark:border-white bg-white dark:bg-slate-900 text-[#0B1220] dark:text-white shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 hover:-translate-y-0.5 transition-all'
                data-testid='nav-mobile-trigger'
                aria-label='Open menu'
              >
                <Menu className='w-4 h-4' />
              </button>
            </SheetTrigger>
            <SheetContent
              side='right'
              className='dark:bg-slate-900 dark:text-slate-100 overflow-y-auto max-h-[100dvh] pb-10'
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
              data-testid='mobile-nav-sheet'
            >
              <div className='mt-2 mb-4'><Logo /></div>
              <div className='flex flex-col gap-1'>
                {links.map((l) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    className='py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] inline-flex items-center gap-2'
                    data-testid={`mobile-${l.testid || l.label.toLowerCase()}`}
                  >
                    <span>{l.label}</span>
                    {l.pulse && (
                      <span
                        aria-hidden='true'
                        className='w-1.5 h-1.5 rounded-full bg-[#C8102E]'
                        style={{ boxShadow: '0 0 0 0 rgba(200,16,46,0.55)', animation: 'k9-workshop-live-pulse 1.8s ease-out infinite' }}
                      />
                    )}
                  </Link>
                ))}
                {/* Location group — states index + Canada + UK + Worldwide */}
                <div className='mt-3 mb-1 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>Location</div>
                {LOCATION_LINKS.map((l) => (
                  <Link
                    key={'mloc-' + l.to + l.label}
                    to={l.to}
                    className='py-2 pl-3 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-[#1B3A87] dark:hover:text-[#93C5FD] border-l border-slate-200 dark:border-slate-700'
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </Link>
                ))}
                {/* Example Website is now a top-level link — no dropdown group. */}
                <div className='border-t border-slate-200 dark:border-slate-700 my-3' />
                {user ? (
                  <>
                    <Button variant='outline' size='sm' onClick={() => navigate(user.role === 'CLIENT' ? '/client' : '/admin')}>
                      <LayoutDashboard className='w-4 h-4 mr-1.5' /> Dashboard
                    </Button>
                    <Button variant='ghost' size='sm' onClick={() => { logout(); navigate('/'); }}>
                      <LogOut className='w-4 h-4 mr-1.5' /> Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant='ghost' size='sm' onClick={() => navigate('/login')}>
                      <LogIn className='w-4 h-4 mr-1.5' /> Sign in
                    </Button>
                    <Button size='sm' className='bg-[#1B3A87] text-white' onClick={() => navigate('/register')}>
                      Get Started
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
